import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Gavel, ShieldCheck, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketCard } from "@/components/MarketCard";
import { SampleBanner } from "@/components/SampleBanner";
import { LatestActivityFeed } from "@/components/LatestActivityFeed";
import { fetchMarket, fetchMarketStats, fetchMarketActivity } from "@/lib/api";
import { money } from "@/lib/format";

const StatTile = ({ label, value }) => (
    <div className="flex flex-col rounded-xl border border-border bg-card px-4 py-3">
        <span className="font-heading text-xl font-bold tabular">{value}</span>
        <span className="text-xs text-muted-foreground">{label}</span>
    </div>
);

/** Home's default tab: the fitness sponsor marketplace at a glance. */
export default function HomeSponsors() {
    const market = useQuery({
        queryKey: ["market", "home"],
        queryFn: () => fetchMarket(),
        refetchInterval: 20000,
    });
    const stats = useQuery({
        queryKey: ["market-stats"],
        queryFn: fetchMarketStats,
        refetchInterval: 20000,
    });
    const activity = useQuery({
        queryKey: ["market-activity", "home"],
        queryFn: () => fetchMarketActivity(8),
        refetchInterval: 15000,
    });

    const listings = market.data?.listings || [];
    const top = [...listings]
        .sort((a, b) => b.current_bid_usd - a.current_bid_usd)
        .slice(0, 6);
    const sponsors = market.data?.sponsors || [];

    // Reuse the shared feed shape (type, display_name, amount, ts).
    const activityItems = (activity.data?.activity || []).map((a) => ({
        id: a.id,
        type: a.type === "listed" ? "new_listing" : "raised_bid",
        display_name: a.display_name,
        amount: a.amount_usd,
        ts: a.ts,
    }));

    return (
        <div>
            <section className="relative hero-wash border-b border-border overflow-hidden">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 relative">
                    <div className="mb-4">
                        <SampleBanner className="inline-flex" />
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">
                        Fitness sponsor marketplace.{" "}
                        <span className="text-brand">Stats on the card. Bids in public.</span>
                    </h1>
                    <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">
                        Athletes list real inventory — race kits, posts, story packs, event banners —
                        with followers, average views and watch time attached. Brands bid from a
                        wallet on a concrete deliverable. No DMs, no guessing.
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <Button
                            asChild
                            size="lg"
                            data-testid="home-market-cta"
                            className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold text-base"
                        >
                            <Link to="/market">
                                Browse live auctions <ArrowRight className="h-4 w-4 ml-1" />
                            </Link>
                        </Button>
                        <Button
                            asChild
                            size="lg"
                            variant="outline"
                            data-testid="home-list-slot-cta"
                            className="font-semibold text-base"
                        >
                            <Link to="/market/new">List a sponsor slot</Link>
                        </Button>
                    </div>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 relative z-10">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {stats.isLoading ? (
                        [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[72px] rounded-xl" />)
                    ) : (
                        <>
                            <StatTile label="Live auctions" value={stats.data?.live_listings ?? 0} />
                            <StatTile label="Creators listed" value={stats.data?.creators ?? 0} />
                            <StatTile label="Sponsors bidding" value={stats.data?.sponsors ?? 0} />
                            <StatTile label="Bid volume" value={money(stats.data?.bid_volume_usd ?? 0)} />
                        </>
                    )}
                </div>
            </section>
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2">
                            <Gavel className="h-5 w-5 text-brand" /> Top open auctions
                        </h2>
                        <Link
                            to="/market"
                            data-testid="home-view-all-link"
                            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                        >
                            View all {listings.length} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                    {market.isLoading ? (
                        <div className="grid sm:grid-cols-2 gap-3">
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-64 rounded-2xl" />
                            ))}
                        </div>
                    ) : top.length === 0 ? (
                        <Card className="p-10 text-center border-dashed">
                            <h3 className="font-heading text-lg font-semibold">No auctions yet</h3>
                            <p className="text-muted-foreground text-sm mt-1 mb-5">
                                Be the first creator to put a slot on the board.
                            </p>
                            <Button asChild className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                                <Link to="/market/new">List a slot</Link>
                            </Button>
                        </Card>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-3">
                            {top.map((listing) => (
                                <MarketCard key={listing.id} listing={listing} />
                            ))}
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-4 space-y-6">
                    <LatestActivityFeed
                        variant="auction"
                        items={activityItems}
                        loading={activity.isLoading}
                        updated={!activity.isFetching}
                    />

                    <Card className="bg-card border-border p-4" data-testid="sponsor-demand">
                        <div className="flex items-center gap-2 mb-3">
                            <Users className="h-4 w-4 text-brand" />
                            <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                                Sponsor demand
                            </h3>
                        </div>
                        {market.isLoading ? (
                            <div className="space-y-2">
                                {[0, 1, 2].map((i) => (
                                    <Skeleton key={i} className="h-9 w-full rounded-lg" />
                                ))}
                            </div>
                        ) : sponsors.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-2">No sponsors registered yet.</p>
                        ) : (
                            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                                {sponsors.map((s) => (
                                    <div key={s.sponsor_id} className="flex items-center gap-2 text-sm">
                                        <span className="font-medium truncate">{s.brand}</span>
                                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
                                            {s.category}
                                        </span>
                                        <span className="ml-auto text-xs tabular shrink-0">
                                            {s.fit_listings} fits
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card className="bg-card border-border p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <ShieldCheck className="h-4 w-4 text-brand" />
                            <h3 className="text-sm font-semibold">How the money moves</h3>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            A bid is a hold, not a charge. Outbid → the hold releases back to your
                            wallet. Auction close captures the winner only. Creator uploads proof,
                            sponsor has 48h to accept or dispute. Platform fee 12% on completed
                            deals. No proof in 7 days → the winner is refunded.
                        </p>
                        <Button asChild variant="link" className="px-0 mt-2 text-brand text-xs">
                            <Link to="/rules">Market rules →</Link>
                        </Button>
                    </Card>
                </aside>
            </section>
        </div>
    );
}

